import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { formatGBP, courierFromPackageSize } from "@/lib/marketplace";
import { useAuth } from "@/context/AuthContext";
import { useKidAuth } from "@/context/KidAuthContext";
import { ArrowLeft, ShoppingBag, Truck, MapPin, Globe, Users, School, Zap } from "lucide-react";
import { clearAppUnreadBadge } from "@/lib/push";

const STATUS_STYLES = {
  paid: ["bg-amber-100 text-amber-800", "Paid — to ship/collect"],
  shipped: ["bg-blue-100 text-blue-800", "Shipped"],
  collected: ["bg-emerald-100 text-emerald-800", "Collected"],
  refunded: ["bg-rose-100 text-rose-800", "Refunded"],
  pending: ["bg-slate-100 text-slate-700", "Pending"],
  cancelled: ["bg-rose-100 text-rose-800", "Cancelled"],
};

export default function Orders() {
  const { user } = useAuth();
  const { kid } = useKidAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iteration 180 — parent-dashboard entry must ALWAYS load the full
  // household orders feed even when a kid session is still cached
  // in the same browser. The rule the router encodes:
  //   • plain `/orders`               → parent view (household feed)
  //   • `/orders?kid=<id>`            → kid drill-down (kid feed)
  //   • pure-kid session (no parent)  → kid view regardless of URL
  // Every other combination falls back sensibly.
  const [searchParams] = useSearchParams();
  const paramKidId = searchParams.get("kid") || searchParams.get("fromKid") || "";
  const hasParent = !!(user && user.id);
  const hasKid = !!(kid && kid.kid_id);
  // Kid view is the "child ledger context" per the spec — only when
  // the URL explicitly carries `?kid=` OR the browser has no parent
  // session at all (Kat/Tom on their own device). Parent Ruth
  // clicking the plain Orders stat card lands here as `kidView=false`.
  const kidView = hasKid && (!!paramKidId || !hasParent);
  const effectiveKidId = paramKidId || (kidView && hasKid ? kid.kid_id : "");
  const [kidName, setKidName] = useState(
    searchParams.get("fromKidName")
    || (kidView && hasKid ? (kid?.name || kid?.kid_name || "") : ""),
  );
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user === null || kid === null) return; // wait for hydration
    let cancelled = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        let rows = [];
        if (kidView && kid?.kid_id) {
          const { data } = await api.get(
            `/kids/${encodeURIComponent(kid.kid_id)}/orders?scope=household`,
          );
          const raw = Array.isArray(data?.rows) ? data.rows : (Array.isArray(data) ? data : []);
          rows = raw.map((r) => ({
            id: r.id,
            listing_id: r.listing_id,
            title: r.item_title || r.title,
            status: r.status,
            role: r.role,
            role_label: r.role === "sold" ? `Sold by ${r.seller_name || "Kid"}` : `Bought by ${r.buyer_name || "Guest"}`,
            gross_pennies: r.price_pennies,
            fee_pennies: r.fee_pennies,
            net_pennies: r.net_pennies,
            buyer_kid_name: r.buyer_name || null,
            seller_kid_name: r.seller_name || null,
            fulfillment_method: r.fulfillment_method,
            package_size: r.package_size,
            origin: r.origin,
            created_at: r.created_at,
            order_group_id: r.order_group_id || r.id,
          }));
        } else if (hasParent) {
          const { data } = await api.get("/orders");
          rows = data || [];
        }
        if (!cancelled) setOrders(rows);
      } catch (e) {
        if (cancelled) return;
        if (!kidView) setErr(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, kid, kidView, hasParent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iteration 250 — viewing the Orders dashboard counts as "seen" for
  // order-related alerts: clear the Home Screen app badge and mark
  // sale/shipping/message notifications read so the Alert Bell resets.
  useEffect(() => {
    if (kidView) return; // kid view has no parent alert feed
    clearAppUnreadBadge();
    api.post("/notifications/mark-read", { kinds: ["sale", "shipping", "message"] })
      .then(() => { window.dispatchEvent(new CustomEvent("mtt:notifications-cleared")); })
      .catch(() => {});
  }, [kidView]);

  useEffect(() => {
    if (!paramKidId || kidName || kidView) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/kids");
        if (cancelled) return;
        const match = (data || []).find((k) => k.kid_id === paramKidId || k.id === paramKidId);
        if (match) setKidName(match.name || match.kid_name || "");
      } catch { /* leave fallback copy */ }
    })();
    return () => { cancelled = true; };
  }, [paramKidId, kidName, kidView]);

  // Iteration 109 — /orders page deduplication (parity with the Parent
  // Dashboard "Sales & purchases" table, iteration 108). Upstream emits
  // TWO perspective rows for an intra-household kid → kid transaction
  // (same `order_group_id`, different roles). We key each row by
  // `<groupId>::<role>` so both perspective rows survive (1 SOLD row +
  // 1 BOUGHT row) while true upstream duplicates collapse to a single
  // row.
  const dedupedOrders = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of (orders || [])) {
      const gid = o.order_group_id || o.transaction_id || o.id;
      const role = o.role || (o.seller_kid_id && o.buyer_kid_id && o.seller_kid_id === o.buyer_kid_id ? "self" : (o.seller_kid_id ? "sold" : "bought"));
      const key = `${gid}::${role}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(o);
    }
    return out;
  }, [orders]);

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        {/* Iteration 176 — Unified back-nav. Parent sessions see all
            three affordances (Parent Dashboard / Ledger & Activity /
            optional Kid Shop Ledger when `?kid=<id>` is set). Pure-kid
            sessions see ONLY the "Back to <Kid>'s Shop Ledger" link
            so Kat isn't teased with parent-only surfaces. */}
        <div className="flex flex-wrap items-center gap-4" data-testid="orders-back-nav">
          {!kidView && (
            <>
              <Link to="/dashboard/parent" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-blue-600" data-testid="orders-back">
                <ArrowLeft className="w-4 h-4" /> Back to Parent Dashboard
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/dashboard/ledger" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-blue-600" data-testid="orders-back-ledger">
                <ArrowLeft className="w-4 h-4" /> Back to Ledger &amp; Activity
              </Link>
            </>
          )}
          {effectiveKidId && (
            <>
              {!kidView && <span className="text-slate-300">·</span>}
              <Link
                to={`/kidzone/ledger?kid=${encodeURIComponent(effectiveKidId)}`}
                className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-brandPink"
                data-testid="orders-back-kid-ledger"
              >
                <ArrowLeft className="w-4 h-4" /> Back to {kidName || "Kid"}&apos;s Shop Ledger
              </Link>
            </>
          )}
        </div>
        <div className="mt-4">
          <div className="pill bg-blue-100 text-blue-800"><ShoppingBag className="w-3 h-3 mr-1" /> Orders</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-3" data-testid="orders-heading">Sales & purchases</h1>
        </div>

        {err && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="orders-error">{err}</div>}

        {/* This section should show ALL the orders of the family — not
            just the ones placed by the active kid (e.g. Tom). Backend
            `resolve_ledger` returns the full household feed under
            `scope=household`, and this list maps every row without
            any kid_id-based client-side filter. */}
        <div className="mt-8 space-y-3" data-testid="orders-list">
          {loading ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="card-base p-6 h-24 animate-pulse" />)
            : dedupedOrders.length === 0 ? (
              <div className="card-base p-10 text-center text-slate-500" data-testid="orders-empty">No orders yet.</div>
          ) : dedupedOrders.map((o) => {
            const [cls, lbl] = STATUS_STYLES[o.status] || ["bg-slate-100 text-slate-700", o.status || "—"];
            // Iteration 104 — Parent Orders feed labels every row with a
            // role tag ("Sold by Kat" / "Bought by Tom") and shows both
            // buyer + seller kid names when known. Sellers see the NET
            // payout (after platform fee); buyers see gross.
            const isSeller = o.role === "sold";
            const roleCls = isSeller ? "bg-emerald-100 text-emerald-800" : (o.role === "bought" ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-700");
            // Iteration 109b — SELLER net math is authoritative from the
            // backend now (mappings.py `_derive_fee_and_net_pennies`).
            // The frontend guarantees the display value is ALWAYS the
            // net payout for sellers: never raw gross. Priority:
            //   1. `net_pennies` (>0) direct from backend
            //   2. `gross - fee` (both from backend)
            //   3. `gross - 10%` (defensive — if backend somehow
            //      returned fee=0/net=0 for a seller row)
            const grossPennies = Number(o.gross_pennies || o.total_pennies || o.amount_pennies || 0);
            const feePennies = Number(o.fee_pennies || o.platform_fee_pennies || 0);
            let sellerNetPennies = Number(o.net_pennies) > 0
              ? Number(o.net_pennies)
              : Math.max(grossPennies - feePennies, 0);
            if (isSeller && sellerNetPennies >= grossPennies && grossPennies > 0) {
              // Defensive: never render gross as "net" — apply the
              // canonical flat £0.30 platform fee (iteration 109c).
              sellerNetPennies = Math.max(grossPennies - 30, 0);
            }
            const displayPennies = isSeller ? sellerNetPennies : grossPennies;
            return (
              <Link key={o.id} to={`/orders/${o.id}`} className="card-base p-5 flex flex-wrap items-center gap-4 hover:-translate-y-0.5 hover:shadow-pop transition-all block" data-testid={`order-row-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {o.role_label && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${roleCls}`} data-testid={`order-role-${o.id}`}>{o.role_label}</span>
                    )}
                    {o.auto_approved && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-teal-100 text-teal-800" data-testid={`order-auto-approved-${o.id}`} title="Completed instantly by your Auto-Approve rule"><Zap className="w-3 h-3" /> Auto-Approved</span>
                    )}
                    <div className="font-display text-lg font-medium text-slate-900 truncate">{o.title || o.listing_title || "Listing"}</div>
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>Buyer: {o.buyer_kid_name || "Guest"}{o.buyer_kid_handle ? ` (@${o.buyer_kid_handle})` : ""}</span>
                    {o.seller_kid_name && <><span>·</span><span data-testid={`order-seller-${o.id}`}>Seller: {o.seller_kid_name}{o.seller_kid_handle ? ` (@${o.seller_kid_handle})` : ""}</span></>}
                    <span>·</span>
                    {o.fulfillment_method === "parent_handoff" ? (
                      <span data-testid={`order-fulfillment-${o.id}`}><Users className="inline w-3 h-3" /> Parent Handoff</span>
                    ) : o.fulfillment_method === "school_gate" || o.fulfillment_method === "collection" ? (
                      <span data-testid={`order-fulfillment-${o.id}`}><School className="inline w-3 h-3" /> School Gate</span>
                    ) : (
                      <span title={courierFromPackageSize(o.package_size).name} data-testid={`order-fulfillment-${o.id}`}>
                        <Truck className="inline w-3 h-3" /> Ship · {courierFromPackageSize(o.package_size).emoji} {courierFromPackageSize(o.package_size).name}
                      </span>
                    )}
                    {o.origin && (<><span>·</span><span className="inline-flex items-center text-blue-700" data-testid={`order-origin-${o.id}`}><Globe className="w-3 h-3 mr-0.5" /> {o.origin}</span></>)}
                  </div>
                </div>
                <div className="text-xl font-display font-semibold text-blue-600" data-testid={`order-amount-${o.id}`}>
                  {formatGBP(displayPennies / 100)}{isSeller && <span className="ml-1 text-[10px] text-slate-500 font-semibold">net</span>}
                </div>
                <span className={`pill ${cls}`}>{lbl}</span>
              </Link>
            );
          })}
        </div>
      </section>
      <Footer />
    </div>
  );
}
